// Quote Builder — Panels & Accessories section, shared.
//
// Lifted out of door-picker.ts (loader ~199-244, markup ~1219-1275, handlers
// ~561-580) so end panels, plinths and cornices for the chosen range are
// reachable from the SJB frontal flow too, rather than being a separate trip
// through the standalone door picker. door-picker.ts renders through here now.
//
// Collapsed by default in both, with the item count in the heading — the
// retailer should be able to see there ARE extras without the section pushing
// the rest of the configurator off screen.

import { api } from "@tkos/api-client";
import { escapeHtml, fmtGbp, ICONS, svgIcon } from "./formatters";
import type { ItemCategory } from "./types";
import type { DoorType } from "./door-colour-select";

export interface ExtraItem {
  id: string;
  sku: string;
  name: string;
  h: number;
  w: number;
  d: number;
  price: number;
  doorRange: string;
  doorColour: string;
  category: string;
  /** The supplier's discount code (PWS "BMV"), served since 2026-10-01. */
  discountCode?: string;
}

/**
 * `type` scopes panels to the active door tier so PTO/bespoke don't surface
 * stock-colour panels. `colour` narrows further only for stock — non-stock
 * panels are tier-scoped, not colour-scoped, so callers pass colour='' there.
 */
export async function fetchExtras(
  slug: string,
  range: string,
  type: DoorType,
  colour: string,
): Promise<ExtraItem[]> {
  if (!range) return [];
  const qs = new URLSearchParams({ supplier: slug, range, type });
  if (colour) qs.set("colour", colour);
  try {
    const data = await api<{ items?: ExtraItem[]; extras?: ExtraItem[] }>(
      `/catalogue/door-range-extras?${qs.toString()}`);
    return data.items ?? data.extras ?? [];
  } catch {
    return [];
  }
}

/** Which quote-line category an extra lands in. */
export function inferExtraCategory(name: string): ItemCategory {
  const n = (name || "").toLowerCase();
  if (n.includes("plinth")) return "plinth";
  if (n.includes("cornice") || n.includes("pelmet")) return "panels";
  if (n.includes("panel")) return "panels";
  return "sundries";
}

export interface ExtrasSectionState {
  extras: ExtraItem[];
  open: boolean;
  loading: boolean;
  /** Per-extra quantity, keyed by id. */
  qtyById: Record<string, number>;
}

export function renderExtrasSection(s: ExtrasSectionState): string {
  const count = s.extras.length;
  const summary = s.loading
    ? " (loading…)"
    : count > 0
      ? ` <span class="qb-door-extras__summary">(${count} item${count === 1 ? "" : "s"})</span>`
      : ` <span class="qb-door-extras__summary qb-door-extras__summary--muted">(none for this range)</span>`;
  const caret = s.open ? "▾" : "▸";

  let body = "";
  if (s.open) {
    if (s.loading) {
      body = `<div class="qb-door-loading">Loading extras…</div>`;
    } else if (count === 0) {
      body = `<div class="qb-door-empty">No matching panels or accessories for this range</div>`;
    } else {
      body = `
        <div class="qb-door-extras__list">
          ${s.extras.map((x) => {
            const dims = (x.h || x.w) ? `${x.h || "?"} × ${x.w || "?"}${x.d ? ` × ${x.d}` : ""}mm · ` : "";
            const qty = s.qtyById[x.id] ?? 1;
            return `
              <div class="qb-door-extras__row">
                <div class="qb-door-extras__main">
                  <div class="qb-door-extras__name">${escapeHtml(x.name)}</div>
                  <div class="qb-door-extras__meta">
                    <span class="qb-door-extras__sku">${escapeHtml(x.sku)}</span>
                    <span class="qb-door-extras__dims">${dims}${escapeHtml(x.category || "")}</span>
                  </div>
                </div>
                <div class="qb-door-extras__price">${fmtGbp(x.price)}</div>
                <input class="qb-door-input qb-door-extras__qty" type="number" min="1" step="1"
                       value="${qty}" data-role="extra-qty" data-extra-id="${escapeHtml(x.id)}" />
                <button type="button" class="qb-btn qb-btn--ghost qb-door-extras__add"
                        data-role="extra-add" data-extra-id="${escapeHtml(x.id)}">
                  ${svgIcon(ICONS.plus)} Add
                </button>
              </div>
            `;
          }).join("")}
        </div>
      `;
    }
  }

  return `
    <div class="qb-door-extras" data-role="extras-section">
      <button type="button" class="qb-door-extras__head" data-role="extras-toggle"
              aria-expanded="${s.open ? "true" : "false"}">
        <span class="qb-door-extras__caret">${caret}</span>
        <span class="qb-door-extras__title">Panels &amp; Accessories${summary}</span>
      </button>
      ${body}
    </div>
  `;
}

export interface ExtrasWireOpts {
  onToggle(): void;
  onQty(id: string, qty: number): void;
  onAdd(item: ExtraItem): void;
}

export function wireExtrasSection(
  host: HTMLElement,
  s: ExtrasSectionState,
  opts: ExtrasWireOpts,
): void {
  host.querySelector<HTMLButtonElement>("[data-role='extras-toggle']")
    ?.addEventListener("click", () => opts.onToggle());
  host.querySelectorAll<HTMLInputElement>("[data-role='extra-qty']").forEach((el) => {
    el.addEventListener("input", () => {
      const id = el.dataset["extraId"] ?? "";
      if (!id) return;
      opts.onQty(id, Math.max(1, Number(el.value) || 1));
    });
  });
  host.querySelectorAll<HTMLButtonElement>("[data-role='extra-add']").forEach((el) => {
    el.addEventListener("click", () => {
      const id = el.dataset["extraId"] ?? "";
      const found = s.extras.find((x) => x.id === id);
      if (found) opts.onAdd(found);
    });
  });
}
